import { store } from '@/store';

export interface PayerRow {
  id: string;
  payerName: string;
  payerId: string;
  tradingPartnerId: string;
  tradingPartnerName: string;
  xifinName?: string;
  workersComp: boolean;
}

export interface PayerSearchOptions {
  workersComp?: boolean;
  requireTradingPartner?: boolean;
}

export interface CoverageResult {
  status: string;
  summary: {
    demographicsFound: boolean;
    discoveredEligibilityCount: number;
    verifiedEligibilityCount: number;
    matchedInsuranceCount: number;
  };
  demographics: { validatedFields: Record<string, string> };
  insuranceDiscovery: Array<{
    hierarchy: string;
    payer: string;
    payerId: string;
    tradingPartnerId: string;
    memberId: string;
    group: string;
    effective: string;
    end: string;
    active: boolean;
  }>;
  eligibilityVerification: Array<{
    match: { matchedToResponse: boolean };
    returned: {
      COBOrder: number | null;
      cobMismatch: boolean;
      isActiveCoverage: boolean;
      payer: string;
      payerId?: string;
      memberId?: string;
      group?: string;
      hierarchy?: string;
      effective?: string;
      end?: string;
    };
  }>;
  messages: Array<{ severity: string; message: string }>;
}

const PAYER_LOOKUP_BY_API_HOST: Record<string, string> = {
  'crm-api-dev.rhythmsynergy.com': 'https://7eiwqhnnaasy2sl2yrdjjx7ptm0btspu.lambda-url.us-east-1.on.aws',
  'api.rhythmsynergy.com': 'https://upybrrcxctjoq7pvuay6kljk640gridu.lambda-url.us-east-1.on.aws'
};

function serviceBase(): string {
  const configured = (import.meta.env.VITE_PAYER_LOOKUP_URL || '').replace(/\/$/, '');
  if (configured) return configured;
  const api = import.meta.env.VITE_APP_API_URL || '';
  try {
    return PAYER_LOOKUP_BY_API_HOST[new URL(api).host] || '';
  } catch {
    return '';
  }
}

function authHeaders(): HeadersInit {
  const token = store.getState().auth.token;
  return token
    ? { authorization: `Bearer ${token}`, 'content-type': 'application/json' }
    : { 'content-type': 'application/json' };
}

function tidy(value?: string | null): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function cleanPayer(row: PayerRow): PayerRow {
  return {
    ...row,
    payerName: tidy(row.payerName),
    payerId: tidy(row.payerId),
    tradingPartnerId: tidy(row.tradingPartnerId),
    tradingPartnerName: tidy(row.tradingPartnerName),
    xifinName: tidy(row.xifinName)
  };
}

/** Payer name only. A trading-partner name is not a stand-in. */
export function payerTitle(row: Partial<PayerRow>): string {
  return tidy(row.payerName);
}

export function payerResultLine(row: Partial<PayerRow>): string {
  const parts = [payerTitle(row)];
  const payerId = tidy(row.payerId);
  const tradingPartnerName = tidy(row.tradingPartnerName);
  const tradingPartnerId = tidy(row.tradingPartnerId);
  if (payerId) parts.push(`payer ID ${payerId}`);
  if (tradingPartnerName && tradingPartnerName.toLowerCase() !== payerTitle(row).toLowerCase()) {
    parts.push(`trading partner ${tradingPartnerName}`);
  }
  if (tradingPartnerId) parts.push(`trading partner ID ${tradingPartnerId}`);
  return parts.filter(Boolean).join(' · ');
}

function payerIdentity(row: PayerRow): string {
  return `${row.payerId.toLowerCase()}|${row.payerName.toLowerCase()}`;
}

function matchesPayer(row: PayerRow, query: string): boolean {
  const q = query.toLowerCase();
  return row.payerName.toLowerCase().includes(q) || row.payerId.toLowerCase().includes(q);
}

export async function searchPayers(term: string, options: PayerSearchOptions = {}): Promise<PayerRow[]> {
  const query = term.trim();
  const base = serviceBase();
  if (query.length < 3 || !base) return [];
  const params = new URLSearchParams({ q: query });
  if (options.workersComp) params.set('workersComp', 'true');
  if (options.requireTradingPartner) params.set('tradingPartner', 'true');
  const response = await fetch(`${base}/payers?${params.toString()}`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Payer lookup failed');
  const rows = (await response.json()) as PayerRow[];
  if (!Array.isArray(rows)) return [];
  const unique = new Map<string, PayerRow>();
  rows.map(cleanPayer).forEach((row) => {
    if (!row.payerName || !row.payerId || !matchesPayer(row, query)) return;
    const identity = payerIdentity(row);
    const existing = unique.get(identity);
    if (!existing || (!existing.tradingPartnerId && row.tradingPartnerId)) unique.set(identity, row);
  });
  return [...unique.values()].sort(
    (a, b) => payerTitle(a).localeCompare(payerTitle(b)) || a.payerId.localeCompare(b.payerId)
  );
}

export async function runCoverage(body: {
  accountNumber?: string;
  dateOfService?: string;
  demographics: Record<string, string>;
  insurances?: Array<Record<string, string>>;
  options: { runInsuranceDiscovery?: boolean; checkEligibility?: boolean; checkDemographics?: boolean };
}): Promise<CoverageResult> {
  const base = serviceBase();
  if (!base) throw new Error('Payer lookup is not configured');
  const response = await fetch(`${base}/coverage`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body)
  });
  if (!response.ok) throw new Error('Coverage check failed');
  return response.json() as Promise<CoverageResult>;
}
